import type { FC, ReactNode } from 'react'
import type { Service } from '@/types'

import { useRef } from 'react'
import Image from 'next/image'
import { ArrowRight, ArrowUpRight, ScanSearch } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { gsap, useGSAP, ScrollTrigger, MOTION_QUERY } from '@/lib/gsap'
import { TransitionLink } from '@/components/generals/transition-link'
import { siteConfig } from '@/constants/site'

interface ShowcaseCardProps {
    service: Service
    index: number
}

const ShowcaseCard: FC<ShowcaseCardProps> = ({ service, index }): ReactNode => {
    const title = service.homeTitle ?? service.title
    const isEmergency = service.formService === 'emergency'

    return (
        <TransitionLink
            href={service.href}
            data-showcase-card
            className='group relative isolate flex h-full flex-col overflow-hidden rounded-[26px] bg-ink text-cream shadow-[0_30px_60px_-36px_rgba(21,23,26,0.6)]'
        >
            <div data-media className='relative -mb-px aspect-[4/3] overflow-hidden bg-ink xl:aspect-[5/4]'>
                <div data-media-inner className='absolute inset-0 [backface-visibility:hidden]'>
                    <Image
                        src={service.homeImage ?? service.image}
                        alt={service.homeImageAlt ?? service.imageAlt}
                        fill
                        sizes='(min-width: 1280px) 25vw, (min-width: 640px) 50vw, 100vw'
                        className='object-cover'
                        style={{ objectPosition: service.homeImagePosition ?? '50% 50%' }}
                    />
                </div>

                <div aria-hidden='true' className='absolute inset-x-0 -bottom-px h-24 bg-gradient-to-t from-ink via-ink/40 to-transparent' />

                <div className='absolute inset-x-4 top-4 flex items-start justify-between gap-3'>
                    {isEmergency ? (
                        <span className='inline-flex items-center gap-2 rounded-full bg-ember px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-white shadow-[0_8px_20px_-8px_rgba(30,58,92,0.7)]'>
                            <span className='relative flex size-1.5' aria-hidden='true'>
                                <span className='absolute inset-0 rounded-full bg-white animate-live' />
                                <span className='relative size-1.5 rounded-full bg-white' />
                            </span>
                            Line open until {siteConfig.emergencyUntil}
                        </span>
                    ) : (
                        <span className='rounded-full bg-ink/75 px-3 py-1.5 font-mono text-[10px] tracking-[0.14em] text-cream ring-1 ring-cream/15'>
                            {pad(index + 1)}
                        </span>
                    )}

                    <span className='flex size-10 shrink-0 items-center justify-center rounded-full bg-ink/60 text-cream ring-1 ring-cream/25 transition-[background-color,color,rotate] duration-500 ease-expo group-hover:rotate-45 group-hover:bg-cream group-hover:text-ink'>
                        <ArrowUpRight className='size-4' aria-hidden='true' />
                    </span>
                </div>
            </div>

            <div data-body className='flex flex-1 flex-col p-5 sm:p-6'>
                <p className='font-mono text-[10.5px] uppercase tracking-[0.16em] text-cream/50'>
                    {service.kind ?? "Service"} · {service.meta}
                </p>

                <h3 className='mt-3 font-heading text-[1.4rem] font-semibold leading-[1.1] tracking-[-0.03em] text-cream sm:text-[1.5rem]'>
                    {title}
                </h3>

                <p className='mb-6 mt-2.5 text-[14.5px] leading-relaxed text-cream/60'>
                    {service.summary}
                </p>

                <div className='mt-auto flex items-center justify-between gap-4 border-t border-cream/10 pt-5'>
                    <span className='font-mono text-[13px] text-cream'>{service.priceLabel}</span>

                    <span className='inline-flex h-10 shrink-0 items-center gap-2 rounded-full bg-cream px-4 text-[13px] font-medium text-ink transition-colors duration-300 group-hover:bg-ember group-hover:text-white'>
                        See the details
                        <ArrowRight className='size-3.5 transition-transform duration-500 ease-expo group-hover:translate-x-0.5' aria-hidden='true' />
                    </span>
                </div>
            </div>
        </TransitionLink>
    )
}

const DiagnosisTile: FC = (): ReactNode => {
    return (
        <TransitionLink
            href='#diagnose'
            data-showcase-card
            className='group relative isolate flex h-full min-h-[22rem] flex-col justify-between overflow-hidden rounded-[26px] bg-ember p-6 text-white transition-colors duration-500 hover:bg-ember-deep sm:p-7'
        >
            <div aria-hidden='true' className='door-grooves pointer-events-none absolute inset-0 opacity-40' />
            <div aria-hidden='true' className='pointer-events-none absolute -bottom-24 -right-24 size-72 rounded-full bg-white/10 blur-3xl' />

            <div data-body className='relative'>
                <span className='flex size-12 items-center justify-center rounded-full bg-white/12 ring-1 ring-white/25'>
                    <ScanSearch className='size-5' aria-hidden='true' />
                </span>

                <p className='mt-8 font-mono text-[10.5px] uppercase tracking-[0.16em] text-white/75'>
                    Door check · 30 seconds
                </p>

                <p className='mt-3 font-heading text-[1.9rem] font-semibold leading-[1.02] tracking-[-0.035em]'>
                    Can't name the problem?
                </p>

                <p className='mt-3 max-w-[28ch] text-[14.5px] leading-relaxed text-white/75'>
                    Choose what the door is doing and see the likely fix, plus a typical price.
                </p>
            </div>

            <span className='relative mt-8 inline-flex h-10 items-center gap-2 self-start rounded-full bg-cream px-4 text-[13px] font-medium text-ink'>
                Find the problem
                <ArrowRight className='size-3.5 transition-transform duration-500 ease-expo group-hover:translate-x-0.5' aria-hidden='true' />
            </span>
        </TransitionLink>
    )
}

interface ServiceShowcaseProps {
    services: Service[]
    className?: string
}

/**
 * Catalog grid of services: one photo per card with the details underneath.
 * Each photo opens from the bottom like a garage door as its row scrolls in.
 */
export const ServiceShowcase: FC<ServiceShowcaseProps> = ({ services, className }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)

    useGSAP(() => {
        const grid = ref.current

        if (!grid) return

        const media = gsap.matchMedia()

        media.add(MOTION_QUERY, () => {
            const cards = gsap.utils.toArray<HTMLElement>('[data-showcase-card]', grid)

            cards.forEach(card => {
                const frame = card.querySelector<HTMLElement>('[data-media]')
                const inner = card.querySelector<HTMLElement>('[data-media-inner]')
                const body = card.querySelector<HTMLElement>('[data-body]')

                if (frame) gsap.set(frame, { clipPath: 'inset(100% 0% 0% 0%)' })
                if (inner) gsap.set(inner, { scale: 1.22 })
                if (body) gsap.set(body, { y: 26, autoAlpha: 0 })
                if (!frame) gsap.set(card, { autoAlpha: 0 })
            })

            ScrollTrigger.batch(cards, {
                start: 'top 90%',
                once: true,
                onEnter: batch => {
                    batch.forEach((card, order) => {
                        const element = card as HTMLElement
                        const frame = element.querySelector<HTMLElement>('[data-media]')
                        const inner = element.querySelector<HTMLElement>('[data-media-inner]')
                        const body = element.querySelector<HTMLElement>('[data-body]')
                        const timeline = gsap.timeline({ delay: order * 0.12 })

                        if (frame) timeline.to(frame, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.35, ease: 'expo.inOut', clearProps: 'clipPath' }, 0)
                        if (inner) timeline.to(inner, { scale: 1, duration: 1.9, ease: 'expo.out', clearProps: 'transform' }, 0.1)
                        if (body) timeline.to(body, { y: 0, autoAlpha: 1, duration: 1.1, ease: 'expo.out', clearProps: 'transform,opacity,visibility' }, 0.45)
                        if (!frame) timeline.to(element, { autoAlpha: 1, duration: 0.9, ease: 'power2.out', clearProps: 'opacity,visibility' }, 0.1)
                    })
                }
            })
        })

        return () => media.revert()
    }, { scope: ref })

    return (
        <div ref={ref} className={cn('grid gap-4 sm:grid-cols-2 xl:grid-cols-4 xl:gap-5', className)}>
            {services.map((service, index) => (
                <ShowcaseCard key={service.slug} service={service} index={index} />
            ))}

            <DiagnosisTile />
        </div>
    )
}
